import React from 'react';
import type { FrequencyEnum } from 'venn-api';
import { ProxyTypeDropdown } from './ProxyTypeDropdown';
import type { SecondLayerProxyType, SecondLayerProxyTypeValue } from 'venn-utils';
import type { LayeredProxyConfiguration } from '../proxy-types';
import type { ReturnsRangeInfo } from '../utils';
import { typeSupportsDesmooth } from '../utils';
import type { FundToBulkProxy, FundToProxy } from '../../types';
import { useSecondLayerProxyTypeDropdownItems } from '../hooks/useProxyTypeDropdownItems';
import { Icon } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import LagInput from '../LagInput';

export const SECOND_LAYER_PROXY_TYPES: SecondLayerProxyType[] = [
  'DESMOOTH',
  'DESMOOTH_INTERPOLATE',
  'INTERPOLATE',
  'EXTRAPOLATE',
];

export type { SecondLayerProxyTypeValue, SecondLayerProxyType };

interface SecondLayerProxyTypeSelectorProps {
  value: SecondLayerProxyTypeValue | undefined;
  onSelect: (value: SecondLayerProxyTypeValue) => void;
  investments: (FundToProxy | FundToBulkProxy)[];
  rawInvestmentRanges: (ReturnsRangeInfo | null)[];
  selectedProxyRange: ReturnsRangeInfo | undefined;
  withExtrapolation: boolean;
  configuration: LayeredProxyConfiguration;
  onRemove: () => void;
  numLags: number;
  suggestedNumLags: number | undefined;
  onLagUpdate: (lags: number) => void;
  frequency: FrequencyEnum | undefined;
}

const isMultiConfiguration = (config: LayeredProxyConfiguration): boolean => config._tag === 'Multi';

export const SecondLayerProxyTypeSelector = ({
  value,
  onSelect,
  investments,
  rawInvestmentRanges,
  selectedProxyRange,
  withExtrapolation,
  configuration,
  onRemove,
  numLags,
  suggestedNumLags,
  onLagUpdate,
  frequency,
}: SecondLayerProxyTypeSelectorProps) => {
  const items = useSecondLayerProxyTypeDropdownItems({
    investments,
    rawInvestmentRanges,
    selectedProxyRange,
    withInvalidTypes: true,
    withExtrapolation,
  });

  const isMulti = isMultiConfiguration(configuration);
  const hasSingleProxy = investments.length === 1;
  const secondLayerProxyType = value?.proxyType;
  const showDesmoothLags = isMulti && secondLayerProxyType && typeSupportsDesmooth(secondLayerProxyType);
  const showLagInputInline = showDesmoothLags && hasSingleProxy;

  return (
    <div className="flex items-end">
      <div className={cn(isMulti && '[&_.qa-dropmenu-trigger]:rounded-r-none [&_.qa-dropmenu-trigger]:border-r-0')}>
        <ProxyTypeDropdown<SecondLayerProxyTypeValue>
          value={value}
          onSelect={onSelect}
          items={items}
          placeholder="Add additional proxy (optional)"
          width={350}
          testId="qa-second-layer-proxy-dropdown"
        />
      </div>
      {showLagInputInline && frequency !== undefined && (
        <LagInput
          frequency={frequency}
          numLags={numLags}
          suggestedNumLags={suggestedNumLags}
          onLagUpdate={onLagUpdate}
          inputClassName="!h-[35px] !rounded-none border border-solid border-venn-grey-30"
          testId="qa-second-layer-lag-input"
          trailingElement={
            <button
              type="button"
              onClick={onRemove}
              className={cn(
                'flex items-center justify-center',
                'h-[35px] w-[35px]',
                'border border-solid border-venn-grey-30 border-l-0',
                'rounded-r rounded-l-none bg-white',
                'hover:bg-venn-grey-10',
                '[&_i]:hover:text-venn-error',
                'cursor-pointer',
              )}
              data-testid="qa-remove-second-layer-button"
              aria-label="Remove second layer proxy"
            >
              <Icon type="trash" />
            </button>
          }
        />
      )}
      {isMulti && !showLagInputInline && (
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'flex items-center justify-center',
            'h-[35px] w-[35px]',
            'border border-solid border-venn-grey-30',
            'rounded-r rounded-l-none bg-white',
            'hover:bg-venn-grey-10',
            '[&_i]:hover:text-venn-error',
            'cursor-pointer',
          )}
          data-testid="qa-remove-second-layer-button"
          aria-label="Remove second layer proxy"
        >
          <Icon type="trash" />
        </button>
      )}
    </div>
  );
};
